import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import { USE_MOCKS } from '@/mocks/fixtures'
import type { CurrentUser } from '@/types/domain'

/* The user's own profile photo (FR-58, AD-18).
 *
 * The upload is three requests and the middle one does not come here: the
 * browser PUTs the bytes STRAIGHT to object storage, and they never pass
 * through the application. Same shape as a case file's upload, and for the same
 * reason — a photo taken on a phone travels once.
 *
 *   1. POST /session/avatar/upload-url  → a ticket: URL, key, expiry, max size
 *   2. PUT  <the ticket's URL>          → bytes, direct to the bucket
 *   3. PUT  /session/avatar             → the key, once the bytes have landed
 *
 * Step 3 is what records the photo. If step 2 fails there is nothing to clean
 * up: nothing was written, and the orphaned object (if any) is swept by storage
 * versioning rather than by us.
 *
 * There is no user id anywhere in this file. A user may only change their OWN
 * photo, and the server takes the subject from the session — so there is no id
 * for this client to pass, or to pass wrongly.
 */

/** What the client may send. Enforced again server-side against the same list:
 *  this is the courtesy that stops a doomed upload, not the control. */
export const ACCEPTED_AVATAR_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
] as const

/** The `accept` attribute for the file input. Derived from the list above so the
 *  picker and the validation cannot disagree. */
export const AVATAR_ACCEPT = ACCEPTED_AVATAR_TYPES.join(',')

/** Mirrors identity.MaxAvatarBytes. Checked here so a 12 MB photo is refused
 *  before it is spent on a phone connection, and again at commit against the
 *  size storage reports — which is the number that actually decides. */
export const MAX_AVATAR_BYTES = 5 * 1024 * 1024

/** The server's pre-signed upload instruction. Held only for the duration of one
 *  upload — the key confers nothing on its own, and the URL expires. */
interface AvatarUploadTicket {
  objectKey: string
  url: string
  expiresAt: string
  maxBytes: number
  method: string
}

/** Why a photo was refused before it was ever sent. The codes are translation
 *  keys, not sentences: this client knows the user's language and the server
 *  deliberately does not compose prose for it (AD-27). */
export type AvatarRejection = 'type' | 'size'

export class AvatarRejectedError extends Error {
  // Declared and assigned rather than a `readonly reason` constructor
  // parameter: parameter properties emit code, which erasableSyntaxOnly forbids.
  readonly reason: AvatarRejection

  constructor(reason: AvatarRejection) {
    super(`The photo was refused (${reason}).`)
    this.name = 'AvatarRejectedError'
    this.reason = reason
  }
}

/** Raised when the bucket refuses the bytes. Distinguished from an API error
 *  because the failure is between the browser and storage — our server never
 *  saw it, so there is no problem document to read. */
export class AvatarUploadFailedError extends Error {
  readonly status: number

  constructor(status: number) {
    super(`The photo could not be sent to storage (${status}).`)
    this.name = 'AvatarUploadFailedError'
    this.status = status
  }
}

/** Checks a file before any request is made.
 *
 *  Exported so the picker can reject without entering the mutation at all — an
 *  upload that cannot succeed should not produce a pending state first. */
export function rejectAvatar(file: File): AvatarRejection | null {
  // Deliberately a whitelist rather than a `startsWith('image/')` test. SVG is
  // an image by every reasonable reading of the word and a script by the
  // browser's, and the photo is rendered from the bucket's origin.
  if (!ACCEPTED_AVATAR_TYPES.includes(file.type as (typeof ACCEPTED_AVATAR_TYPES)[number])) {
    return 'type'
  }
  if (file.size > MAX_AVATAR_BYTES) return 'size'
  return null
}

/** Uploads a profile photo through the three-step (FR-58, AD-18).
 *
 *  NOT optimistic. A local object URL shown before the bytes land would be a
 *  picture only this browser can see, on a page whose whole job is to confirm
 *  what the account actually holds — and it would survive a failed upload
 *  looking exactly like a success. The pending state is shown as pending.
 */
export function useUploadAvatar() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (file: File): Promise<CurrentUser> => {
      const rejection = rejectAvatar(file)
      if (rejection) throw new AvatarRejectedError(rejection)

      if (USE_MOCKS) {
        const current = queryClient.getQueryData<CurrentUser>(queryKeys.session)
        if (!current) throw new Error('no session in the mock cache')
        // A local preview is honest in mock mode: there is no bucket, so this
        // IS the photo for as long as the tab lives.
        return { ...current, avatarUrl: URL.createObjectURL(file) }
      }

      // (1) Ask for somewhere to put it. The server names the key; we never do.
      const ticket = await api.post<AvatarUploadTicket>(
        '/session/avatar/upload-url',
        { fileName: file.name, contentType: file.type },
      )

      // (2) Straight to the bucket.
      //
      // Bare fetch rather than the api client: this request does not go to our
      // API, must not carry our session cookie to a third-party host, and its
      // failure is an HTTP status from storage rather than a problem document.
      const upload = await fetch(ticket.url, {
        method: ticket.method,
        body: file,
        headers: file.type ? { 'Content-Type': file.type } : undefined,
      })
      if (!upload.ok) {
        throw new AvatarUploadFailedError(upload.status)
      }

      // (3) Record it. The server re-verifies the key against this user's own
      // prefix and confirms with storage that the object landed, then answers
      // the updated session — so the photo appears without a second request.
      return api.put<CurrentUser>('/session/avatar', {
        objectKey: ticket.objectKey,
        contentType: file.type,
      })
    },

    onSuccess: (user) => {
      // Seed rather than invalidate: the commit already answered the session,
      // and refetching would spend a round trip to be told the same thing.
      queryClient.setQueryData(queryKeys.session, user)
    },
  })
}

/** Removes the profile photo, returning the interface to initials. */
export function useRemoveAvatar() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (): Promise<void> => {
      if (!USE_MOCKS) await api.delete<void>('/session/avatar')
    },

    onSuccess: () => {
      // Cleared locally rather than refetched. The server answers 204 — there
      // is nothing left to describe — and the one field that changed is one
      // this client can drop itself.
      queryClient.setQueryData<CurrentUser>(queryKeys.session, (user) =>
        user ? { ...user, avatarUrl: undefined } : user,
      )
    },
  })
}
